import React from "react";
import { createPortal } from "react-dom";
import { REACTION_EMOJIS } from "./utils/pickerData";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getVisualViewport() {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, width: 1280, height: 720 };
  }
  const vv = window.visualViewport;
  return {
    left: Number(vv?.offsetLeft || 0),
    top: Number(vv?.offsetTop || 0),
    width: Number(vv?.width || window.innerWidth || document.documentElement.clientWidth || 1280),
    height: Number(vv?.height || window.innerHeight || document.documentElement.clientHeight || 720),
  };
}

/**
 * Telegram-like compact reaction picker.
 * Rendered through a portal so it is never clipped by message bubbles, scroll areas or sidebars.
 */
export default function ReactionPicker({ message, anchor, onSelect, onClose }) {
  const reactions = REACTION_EMOJIS;
  const closeTimerRef = React.useRef(null);
  const isCoarsePointer =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: coarse)").matches
      : false;

  const viewport = getVisualViewport();
  const viewportLeft = viewport.left;
  const viewportTop = viewport.top;
  const vw = viewport.width;
  const vh = viewport.height;
  const isMobileViewport = isCoarsePointer || vw <= 768;

  const anchorCenterX = Number(anchor?.x ?? 0) || viewportLeft + vw / 2;
  const anchorTop = Number(anchor?.top ?? anchor?.y ?? viewportTop + vh / 2);
  const anchorBottom = Number(anchor?.bottom ?? anchor?.y ?? anchorTop + 44);
  const anchorWidth = Number(anchor?.width ?? 0);

  const sidePad = isMobileViewport ? 8 : 14;
  const spacing = isMobileViewport ? 10 : 8;
  const pickerHeight = isMobileViewport ? 58 : 54;
  const maxPickerWidth = Math.max(220, vw - sidePad * 2);
  const preferredWidth = anchorWidth
    ? Math.max(246, Math.min(isMobileViewport ? 356 : 430, anchorWidth))
    : 344;
  const pickerWidth = Math.min(maxPickerWidth, preferredWidth);
  const safeX = clamp(
    anchorCenterX,
    viewportLeft + sidePad + pickerWidth / 2,
    viewportLeft + vw - sidePad - pickerWidth / 2
  );

  const freeAbove = anchorTop - viewportTop - spacing - pickerHeight;
  const freeBelow = viewportTop + vh - anchorBottom - spacing - pickerHeight;
  const preferAbove = String(anchor?.placement || "above") !== "below";
  const placeAbove = preferAbove ? freeAbove >= 8 || freeAbove >= freeBelow : !(freeBelow >= 8) && freeAbove > freeBelow;
  const placement = placeAbove ? "above" : "below";
  const rawTop = placement === "above" ? anchorTop - pickerHeight - spacing : anchorBottom + spacing;
  const safeTop = clamp(rawTop, viewportTop + sidePad, viewportTop + vh - pickerHeight - sidePad);

  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const picker = (
    <div
      className={`reaction-picker reaction-picker--floating reaction-picker--${placement} visible`}
      data-message-id={message?.id || ""}
      data-placement={placement}
      style={{
        "--rp-left": `${safeX}px`,
        "--rp-top": `${safeTop}px`,
        "--rp-width": `${pickerWidth}px`,
        "--rp-transform": "translate3d(-50%, 0, 0)",
      }}
      onClick={(e) => e.stopPropagation()}
      onPointerEnter={() => {
        if (isCoarsePointer) return;
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
      }}
      onPointerLeave={() => {
        if (isCoarsePointer) return;
        closeTimerRef.current = window.setTimeout(() => onClose?.(), 360);
      }}
    >
      <div
        className="reaction-picker__rail"
        onWheel={(e) => {
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.currentTarget.scrollLeft += e.deltaY;
            e.preventDefault();
          }
        }}
      >
        {reactions.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="reaction-picker-btn"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(emoji, e);
              onClose?.();
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  if (typeof document === "undefined" || !document.body) return picker;
  return createPortal(picker, document.body);
}
